import {
  DynamicObjectInterface,
  FieldType,
  PersonalDetailsType,
  RegistrationValidations,
  ValidationsForType,
} from '@commerce-profile-webcomponents/sdk';
import {
  CalendarWidth,
  CalendarYearDropdownType,
  DatePicker,
  DatePickerType,
  FAIcon,
  Input,
  MLPVersion,
  Theme,
  useTranslation,
} from '@nextgen-web-framework/all';
import { memo, useMemo } from 'react';
import { Field, FieldInputProps, FieldProps, FormikContextType, useField, useFormikContext } from 'formik';
import { getDropdownYears, appendSlashInDOB } from '../../utils/profileUtils';
import { useRegistrationFieldsMasterData } from '../../utils/masterdata';
import { checkIfDateIsFormatted, convertDateObject, validateDOB } from '../../utils/registrationUtils';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import { DATE_FORMAT, VALIDATION_VALID_STRING } from '../../utils/Constants';
import styles from './RegistrationBirthDate.module.scss';
import { IRegistrationBirthDate } from './types/PropTypes';

const RegistrationBirthDate = ({
  name,
  selectedAccountType,
  isLunar,
  dateFormat = DATE_FORMAT['DD/MM/YYYY'],
  mlpVersionProp = MLPVersion.TWO,
}: IRegistrationBirthDate) => {
  const t = useTranslation();
  const registrationFieldsMasterData = useRegistrationFieldsMasterData();

  const [, , helpers] = useField(name);
  const { handleBlur, handleChange, status, setFieldValue }: FormikContextType<PersonalDetailsType> =
    useFormikContext();
  const [, dobValidatorMeta] = useField('dobValidator');

  const configs = ConfigService.getConfigs();
  const mlpVersionConfig = configs?.NEXT_INPUT_MLP_VERSION as MLPVersion;
  const mlpVersion = mlpVersionConfig ?? mlpVersionProp;

  const dobInputValidationSchema: RegistrationValidations | undefined = registrationFieldsMasterData?.find(
    (input) => input.fieldName === name,
  );

  const dobYearList = useMemo(() => {
    if (dobInputValidationSchema) {
      const maxAge = Number(
        dobInputValidationSchema?.validations?.[selectedAccountType as keyof ValidationsForType]?.max ?? 0,
      );
      const minAge = Number(
        dobInputValidationSchema?.validations?.[selectedAccountType as keyof ValidationsForType]?.min ?? 0,
      );
      const yearList = getDropdownYears(maxAge, minAge);
      return yearList;
    }
    return [];
  }, [dobInputValidationSchema]);

  const dobFieldType = useMemo(
    () => dobInputValidationSchema?.validations?.[selectedAccountType as keyof ValidationsForType]?.fieldType,
    [dobInputValidationSchema],
  );

  function checkExistingValue(date: DynamicObjectInterface) {
    if (!date) {
      return undefined;
    }

    if (checkIfDateIsFormatted(date, DATE_FORMAT['YYYY-MM-DD'])) {
      return convertDateObject(date).toDate();
    }
    return date;
  }

  const onYearChange = (item: DynamicObjectInterface, field: FieldInputProps<PersonalDetailsType>) => {
    const newDate = field.value ? new Date(field.value.toString()) : new Date();
    newDate.setFullYear(item.value);
    onBirthDateChangeHandler(newDate);
  };

  const appendSlashHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    setFieldValue('dobValidator', VALIDATION_VALID_STRING);
    const { newCursorPosition, formattedValue } = appendSlashInDOB(e);
    await helpers.setTouched(true);
    await helpers.setValue(formattedValue);
    e.target.setSelectionRange(newCursorPosition, newCursorPosition);
  };

  const validateBirthDate = (field: FieldInputProps<PersonalDetailsType>, e: React.FocusEvent<Element>) => {
    const dobValidateObj = validateDOB(
      field.value.toString(),
      selectedAccountType,
      registrationFieldsMasterData,
      isLunar,
      false,
      t,
    );
    if (dobValidateObj.msg) {
      setFieldValue('dobValidator', dobValidateObj.msg);
    } else {
      handleBlur(e);
    }
  };

  const onBirthDateChangeHandler = async (
    date?: Date,
    e?: React.KeyboardEvent | React.MouseEvent | React.FocusEvent,
  ) => {
    e?.stopPropagation();
    e?.preventDefault();
    await helpers.setValue(date);
    if (date) {
      const formattedDate = convertDateObject(date).format(dateFormat);
      const dobValidateObj = validateDOB(
        formattedDate,
        selectedAccountType,
        registrationFieldsMasterData,
        false,
        false,
        t,
      );
      if (dobValidateObj.msg) {
        setFieldValue('dobValidator', dobValidateObj.msg);
      } else {
        helpers.setError('');
        setFieldValue('dobValidator', VALIDATION_VALID_STRING);
      }
    }
  };

  return (
    <Field type="text" name={name}>
      {({ meta, field }: FieldProps) => (
        <>
          {dobFieldType !== FieldType.CALENDER ? (
            <>
              <Field type="hidden" name="dobValidator" />
              <Input
                id={name}
                data-testid={name}
                name={name}
                label={t('profile.RegistrationPersonalDetailsStep.birthDate')}
                onChange={(e) => {
                  appendSlashHandler(e);
                }}
                value={field.value?.toString()}
                onBlur={(e) => {
                  validateBirthDate(field, e);
                }}
                isLunarFormatted={true}
                placeholder={dateFormat}
                mlpVersion={mlpVersion}
                status={inputErrorStatus(meta.touched, meta.error || dobValidatorMeta.error)}
                autoComplete="off"
                message={meta.touched ? inputErrorMessage(t, meta.touched, meta.error) || dobValidatorMeta.error : ''}
                disabled={!status?.enableField}
              />
            </>
          ) : (
            <>
              <div
                className={`${styles['date-picker-input']}} ${
                  inputErrorStatus(meta.touched, meta.error) ? styles['error'] : ''
                }`}
              >
                <DatePicker
                  calendarHeading={t('profile.RegistrationBirthDate.calanderHeading')}
                  inputContainerHeight="56px"
                  calendarWidth={CalendarWidth.FULL_WIDTH}
                  placeholder={t(`profile.RegistrationPersonalDetailsStep.${dateFormat}`)}
                  showFullCalendar={true}
                  type={DatePickerType.DROP_DOWN}
                  scheduleLabel={t('profile.RegistrationBirthDate.selectButton')}
                  cancelLabel={t('profile.RegistrationBirthDate.cancelButton')}
                  showYearDropdown={CalendarYearDropdownType.CUSTOM}
                  onCalendarToggle={() => helpers.setTouched(true)}
                  onChange={(
                    date?: Date,
                    _?: Date,
                    __?: string,
                    ___?: string,
                    e?: React.KeyboardEvent | React.MouseEvent | React.FocusEvent,
                  ) => onBirthDateChangeHandler(date, e)}
                  yearDropdownData={dobYearList}
                  yearDropdownConfigData={{
                    label: 'value',
                    onSelect: (item) => onYearChange(item, field),
                    defaultYear: Number(dobYearList?.[0]?.value),
                  }}
                  disabled={!status?.enableField}
                  value={checkExistingValue(field.value)}
                  timeLabel={''}
                  timeLabelStart={''}
                  timeLabelEnd={''}
                  status={inputErrorStatus(meta.touched, meta.error || dobValidatorMeta.error)}
                  mlpVersion={mlpVersion}
                />
              </div>
              {(meta.error || dobValidatorMeta.error) && meta.touched && (
                <span
                  role="presentation"
                  aria-label="message"
                  className={`${styles['float-input-message']}`}
                  data-testid="message-id"
                >
                  <FAIcon iconName={'circle-check'} theme={Theme.RED} className={styles['input-error-icon']} />
                  <span className={styles['float-error-message']}>
                    {meta.error ? t(meta.error) : dobValidatorMeta.error}
                  </span>
                </span>
              )}
            </>
          )}
        </>
      )}
    </Field>
  );
};

export default memo(RegistrationBirthDate);
